import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Button } from "../ui/button";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { cn, formatter } from "@/lib/utils";
import { airtelLogo, etisalatLogo, gloLogo, mtnLogo } from "@/assets";
import Notiflix from "notiflix";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { numberPrefix } from "@/constants";
import { useMediaQuery } from "react-responsive";

const AirtimeDrawer = ({
  isFormValid,
  phone,
  network,
  amount,
  telcos,
  loading,
  onSubmit,
  handleSubmit,
  openAirtimeDrawer,
  setOpenAirtimeDrawer,
}) => {
  const user = useSelector((state) => state.user.user);
  const [isNumberValid, setIsNumberValid] = useState(null);

  const isMobile = useMediaQuery({ query: "(max-width: 400px)" });

  const validatePhoneNumber = () => {
    if (!phone || !network || !telcos) return false;
    const selectedNetwork = telcos[network]?.toLowerCase();
    if (!selectedNetwork) return false;

    const prefix = phone.substring(1, 4);
    // console.log(prefix);

    return numberPrefix[selectedNetwork]?.includes(prefix) || false;
  };

  useEffect(() => {
    setIsNumberValid(validatePhoneNumber());
  }, [phone, network, telcos]);

  // In the handlePurchaseClick function, modify it like this:
  const handlePurchaseClick = () => {
    if (!isNumberValid && phone && network && telcos) {
      const selectedNetwork = telcos[network]?.toLowerCase();

      Notiflix.Confirm.show(
        "Number Validation",
        `Our system validator detected that this number is not a ${selectedNetwork} number. Do you wish to continue?`,
        "Yes",
        "No",
        () => {
          // User clicked Yes
          // Ensure drawer stays open during form submission
          setOpenAirtimeDrawer(true);
          handleSubmit(onSubmit)();
        },
        () => {
          // User clicked No
          // Explicitly keep the drawer open
          setOpenAirtimeDrawer(false);
          return;
        },
        {
          titleColor: "#FF5F57",
          okButtonBackground: "#FF5F57",
          borderRadius: "8px",
          // Add this to prevent backdrop clicks from closing the confirmation
          backOverlay: true,
          // Prevent Notiflix from closing the drawer
          onClose: () => {
            setOpenAirtimeDrawer(true);
          },
        },
      );
    } else {
      handleSubmit(onSubmit)();
    }
  };

  const showImage = (network) => {
    switch (network) {
      case "mtn":
        return (
          <img src={mtnLogo} alt="mtn" className="size-[25px] rounded-full" />
        );
      case "glo":
        return (
          <img src={gloLogo} alt="glo" className="size-[25px] rounded-full" />
        );
      case "airtel":
        return (
          <img
            src={airtelLogo}
            alt="airtel"
            className="size-[25px] rounded-full"
          />
        );
      case "9mobile":
        return (
          <img
            src={etisalatLogo}
            alt="9mobile"
            className="size-[25px] rounded-full"
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <button
        type="button"
        className={`bg-primary cursor-pointer rounded-md py-2.5 text-white ${!isFormValid && "cursor-not-allowed opacity-50"}`}
        disabled={!isFormValid}
        onClick={() => {
          setOpenAirtimeDrawer(true);
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center gap-2">
            <span className="animate-spin">
              <Loader2 />
            </span>
            <span>Processing...</span>
          </div>
        ) : (
          <span>Preview airtime purchase</span>
        )}
      </button>
      <Drawer
        open={openAirtimeDrawer}
        onOpenChange={() => {
          setOpenAirtimeDrawer(false);
        }}
      >
        <DrawerContent>
          <div className="mx-auto w-full max-w-sm">
            <div className={cn("space-y-4 py-4", isMobile ? "p-4" : "px-0")}>
              <div className="flex items-center justify-center">
                <h3 className="text-3xl font-semibold">
                  <span className="text-xl">₦ </span>
                  {formatter.format(amount)}.00
                </h3>
              </div>

              <div className="mt-10 flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Product Name
                </span>
                <div className="flex items-center gap-1">
                  {showImage(telcos?.[network]?.toLowerCase())}
                  <p className="">Airtime</p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Recipient Mobile
                </span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{phone}</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        {" "}
                        {isNumberValid !== null &&
                          (isNumberValid ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500" />
                          ))}
                      </TooltipTrigger>
                      <TooltipContent className="">
                        <p>Number Validator</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-100">
                  Amount
                </span>
                <span className="font-medium">₦{formatter.format(amount)}</span>
              </div>
            </div>

            <DrawerFooter className={cn("", isMobile ? "p-4" : "px-0")}>
              <button
                disabled={loading}
                onClick={handlePurchaseClick}
                className="bg-primary mb-2 cursor-pointer rounded-lg py-2 font-medium text-white disabled:opacity-60"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <span className="animate-spin">
                      <Loader2 />
                    </span>
                    <span>Processing...</span>
                  </div>
                ) : (
                  <span>Purchase</span>
                )}
              </button>

              <DrawerClose asChild>
                <Button variant="outline">Cancel</Button>
              </DrawerClose>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default AirtimeDrawer;
